import React from 'react'
import PropTypes from 'prop-types'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import Checkbox from '@mui/material/Checkbox'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FixedSizeList } from 'react-window'

const LISTBOX_PADDING = 8
const DEFAULT_ITEM_SIZE = 36
const DEFAULT_LIST_HEIGHT = 280

/**
 * Listbox virtualizado para usar con MUI Autocomplete.
 * Renderiza solo los ítems visibles para listas largas.
 */
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, itemSize: itemSizeProp, listHeight: listHeightProp, ...other } = props
  const itemData = React.Children.toArray(children)
  const itemCount = itemData.length
  const itemSize = itemSizeProp ?? DEFAULT_ITEM_SIZE
  const maxListHeight = listHeightProp ?? DEFAULT_LIST_HEIGHT

  const getHeight = () => {
    if (itemCount === 0) return 0
    const contentHeight = itemCount * itemSize + 2 * LISTBOX_PADDING
    return Math.min(contentHeight, maxListHeight)
  }

  return (
    <ul {...other} role="listbox" style={{ padding: 0, margin: 0, ...other.style }}>
      {itemCount > 0 ? (
        <FixedSizeList
          height={getHeight()}
          width="100%"
          itemCount={itemCount}
          itemSize={itemSize}
          overscanCount={5}
          outerRef={ref}
        >
          {({ index, style }) => {
            const child = itemData[index]
            const inlineStyle = {
              ...style,
              top: (style.top ?? 0) + LISTBOX_PADDING
            }
            return React.isValidElement(child)
              ? React.cloneElement(child, { style: inlineStyle })
              : child
          }}
        </FixedSizeList>
      ) : null}
    </ul>
  )
})

ListboxComponent.propTypes = {
  children: PropTypes.node
}

/**
 * Autocomplete reutilizable con opciones virtualizadas.
 * Usa MUI Autocomplete + react-window para listas largas sin degradar rendimiento.
 *
 * @param {Array} options - Lista de opciones (objetos o strings)
 * @param {Function} getOptionLabel - (option) => string
 * @param {*} value - Valor seleccionado
 * @param {Function} onChange - (event, newValue) => void
 * @param {boolean} loading - Muestra CircularProgress
 * @param {Function} renderOption - (props, option) => ReactNode (opcional)
 * @param {Function} filterOptions - (options, state) => options (opcional, por defecto filtrado local)
 * @param {string} label - Etiqueta del campo
 * @param {string} placeholder - Placeholder del input (usa t('write') por defecto)
 * @param {boolean} disabled
 * @param {object} sx - Estilos MUI
 * @param {number} listHeight - Altura máxima del listbox en px
 * @param {number} itemSize - Altura de cada ítem en px
 * @param {boolean} multiple - Selección múltiple
 * @param {boolean} freeSolo - Permite valor no existente en opciones
 * @param {Function} isOptionEqualToValue - (option, value) => boolean
 * @param {string} noOptionsText - Texto cuando no hay opciones (usa t('no options') por defecto)
 * @param {Function} isValueEmpty - (value) => boolean. Si true, el valor se trata como vacío (evita error con {id:-1, name:''}).
 */
function AutocompleteVirtualized(props) {
  const {
    options = [],
    getOptionLabel,
    value,
    onChange,
    loading = false,
    renderOption,
    filterOptions,
    label = '',
    placeholder,
    disabled = false,
    sx = {},
    listHeight = DEFAULT_LIST_HEIGHT,
    itemSize = DEFAULT_ITEM_SIZE,
    multiple = false,
    freeSolo = false,
    isOptionEqualToValue,
    isValueEmpty = (v) => v != null && typeof v === 'object' && v.id === -1 && (v.name === '' || v.name === undefined),
    noOptionsText,
    inputValue,
    onInputChange,
    direction = 'column',
    justifyContent = 'space-between',
    alignItems = 'stretch',
    fullWidth = true,
    size = 'small',
    variant = 'outlined',
    ...rest
  } = props

  const { t } = useTranslation()

  const resolveGetOptionLabel = (option) => {
    if (typeof getOptionLabel === 'function') return getOptionLabel(option)
    if (typeof option === 'string') return option
    return option?.label ?? String(option ?? '')
  }

  const normalizedValue = multiple
    ? (Array.isArray(value) ? value : value != null ? [value] : []).filter((v) => !isValueEmpty(v))
    : (isValueEmpty(value) ? null : value)

  const selectedValues = Array.isArray(normalizedValue) ? normalizedValue : normalizedValue != null ? [normalizedValue] : []

  const defaultRenderOption = (optionProps, option) => {
    const { key, ...restOptionProps } = optionProps
    const optionLabel = resolveGetOptionLabel(option)
    const isSelected = multiple && selectedValues.some(
      (v) => (isOptionEqualToValue ? isOptionEqualToValue(option, v) : v === option)
    )
    return (
      <Box key={key} component="li" {...restOptionProps} sx={{ display: 'flex', alignItems: 'center', gap: 1, ...restOptionProps.sx }}>
        {multiple && (
          <Checkbox
            size="small"
            checked={isSelected}
            sx={{ py: 0, px: 0.5 }}
          />
        )}
        <label style={{ flex: 1, margin: 0 }}>{optionLabel}</label>
      </Box>
    )
  }

  const tooltipTitle =
    multiple && selectedValues.length > 0 ? (
      <Box component="span" sx={{ py: 0.5 }}>
        {selectedValues.map((opt, i) => (
          <Typography key={i} component="span" display="block" variant="body2">
            • {resolveGetOptionLabel(opt)}
          </Typography>
        ))}
      </Box>
    ) : null

  return (
    <Stack
      className="Field"
      gap={2}
      sx={sx}
      direction={direction}
      justifyContent={justifyContent}
      alignItems={alignItems}
    >
      {label ? <label>{label}</label> : null}
      <Autocomplete
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        sx={{ minWidth: 120 }}
        multiple={multiple}
        freeSolo={freeSolo}
        value={normalizedValue}
        onChange={onChange}
        inputValue={inputValue}
        onInputChange={onInputChange}
        options={options}
        loading={loading}
        getOptionLabel={getOptionLabel ?? resolveGetOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        filterOptions={filterOptions}
        noOptionsText={noOptionsText ?? t('no options')}
        renderOption={renderOption ?? defaultRenderOption}
        ListboxComponent={ListboxComponent}
        ListboxProps={{
          listHeight,
          itemSize,
        }}
        renderTags={(value) => (
          <Box
            component="span"
            className='AutocompleteVirtualized-tag'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              padding: '2.5px 4px 2.5px 6px',
              maxWidth: 'calc(100% - 80px)',
              minWidth: 0,
              font: 'inherit'
            }}
          >
            {value.map((option) => resolveGetOptionLabel(option)).join(', ')}
          </Box>
        )}
        renderInput={(params) => {
          const { key, ...restParams } = params
          const textField = (
            <TextField
              {...restParams}
              variant={variant}
              label={t('write')}
              inputProps={{
                ...restParams.inputProps,
                style: {
                  ...restParams.style,
                },
              }}
              InputProps={{
                ...restParams.InputProps,
                sx: {
                  ...restParams.InputProps?.sx,
                  alignItems: 'center',
                  ...(multiple && {
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: size === 'small' ? 40 : 56,
                    maxHeight: size === 'small' ? 40 : 56,
                    '& .MuiInputBase-input': {
                      position: 'relative',
                      display: 'block',
                      height: '100%',
                      minHeight: size === 'small' ? 35 : 51,
                      maxHeight: size === 'small' ? 35 : 51,
                    },
                    '& .AutocompleteVirtualized-tag': {
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      zIndex: 0
                    },
                    '& input': {
                      position: 'absolute',
                      left: 0,
                      top: '-6px',
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '100%',
                      minWidth: 0,
                      background: 'transparent',
                      zIndex: 1
                    },
                    '&:focus-within .AutocompleteVirtualized-tag': {
                      display: 'none'
                    }
                  })
                },
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {restParams.InputProps?.endAdornment}
                  </React.Fragment>
                )
              }}
            />
          )
          return tooltipTitle ? (
            <Tooltip title={tooltipTitle} placement="top-start" enterDelay={300}>
              {textField}
            </Tooltip>
          ) : (
            textField
          )
        }}
        {...rest}
      />
    </Stack>
  )
}

AutocompleteVirtualized.propTypes = {
  options: PropTypes.array,
  getOptionLabel: PropTypes.func,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  renderOption: PropTypes.func,
  filterOptions: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
  listHeight: PropTypes.number,
  itemSize: PropTypes.number,
  multiple: PropTypes.bool,
  freeSolo: PropTypes.bool,
  isOptionEqualToValue: PropTypes.func,
  isValueEmpty: PropTypes.func,
  noOptionsText: PropTypes.string,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  direction: PropTypes.string,
  justifyContent: PropTypes.string,
  alignItems: PropTypes.string,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard'])
}

export default AutocompleteVirtualized
